import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Button,
  Divider,
  Grid,
  Chip,
  IconButton,
  TextField,
  Avatar,
} from "@mui/material";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from "@mui/lab";
import {
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Send as SendIcon,
  Map as MapIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";
import { SeverityBadge, StatusBadge, TypeBadge } from "../components";
import { getRiskById } from "../data/risksData";
import { CreateWorkOrderDialog } from "../../workorders/components/CreateWorkOrderDialog";
import { format, formatDistanceToNow } from "date-fns";

export const RiskDetail = () => {
  const { riskId } = useParams<{ riskId: string }>();
  const navigate = useNavigate();
  const risk = getRiskById(riskId || "");
  const [woDialogOpen, setWoDialogOpen] = useState(false);

  if (!risk) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h5" color="text.secondary">
          Alert not found
        </Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/risks")} sx={{ mt: 2 }}>
          Back to Alerts
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header with quick actions */}
      <Card variant="outlined" sx={{ mb: 2, flexShrink: 0 }}>
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
            <IconButton onClick={() => navigate("/risks")} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ fontFamily: "monospace" }}>{risk.id}</Typography>
            <SeverityBadge severity={risk.severity} />
            <StatusBadge status={risk.status} />
            <TypeBadge type={risk.type} />
            <Typography variant="body2" color="text.secondary">
              {formatDistanceToNow(new Date(risk.timestamp), { addSuffix: true })}
            </Typography>

            <Box sx={{ flex: 1 }} />

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button size="small" variant="outlined" startIcon={<MapIcon />} onClick={() => navigate("/geospatial")}>
                Map
              </Button>
              <Button size="small" variant="outlined" startIcon={<AssignmentIcon />} onClick={() => setWoDialogOpen(true)}>
                Work Order
              </Button>
              <Button size="small" variant="outlined" startIcon={<GroupIcon />}>
                Dispatch
              </Button>
              <Button size="small" variant="outlined" color="error" startIcon={<WarningIcon />}>
                Escalate
              </Button>
              {risk.status === "active" && (
                <Button size="small" variant="contained" startIcon={<CheckCircleIcon />}>
                  Acknowledge
                </Button>
              )}
              {risk.status === "acknowledged" && (
                <Button size="small" variant="contained" color="warning" startIcon={<EditIcon />}>
                  Investigate
                </Button>
              )}
              {risk.status === "investigating" && (
                <Button size="small" variant="contained" color="success" startIcon={<CheckCircleIcon />}>
                  Resolve
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Main content area with independent scrolling columns */}
      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        {/* Left Column - scrollable */}
        <Grid size={{ xs: 12, lg: 8 }} sx={{ height: "100%" }}>
          <Box sx={{ height: "100%", overflow: "auto", pr: 1 }}>
            <Stack spacing={2}>
              {/* Alert Details Card */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>{risk.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {risk.description}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Location</Typography>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <LocationIcon fontSize="small" color="action" />
                        <Typography variant="body2">{risk.location}</Typography>
                      </Stack>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Region</Typography>
                      <Typography variant="body2">{risk.region || "—"}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Pipeline Segment</Typography>
                      <Chip label={risk.pipelineSegment || "N/A"} size="small" variant="outlined" />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Coordinates</Typography>
                      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                        {risk.coordinates[1].toFixed(4)}, {risk.coordinates[0].toFixed(4)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Detected</Typography>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <ScheduleIcon fontSize="small" color="action" />
                        <Typography variant="body2">{format(new Date(risk.timestamp), "PP p")}</Typography>
                      </Stack>
                    </Grid>
                    {risk.acknowledgedAt && (
                      <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">Acknowledged</Typography>
                        <Typography variant="body2">{format(new Date(risk.acknowledgedAt), "PP p")}</Typography>
                      </Grid>
                    )}
                    {risk.resolvedAt && (
                      <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">Resolved</Typography>
                        <Typography variant="body2">{format(new Date(risk.resolvedAt), "PP p")}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              {/* Assignment Card */}
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Stack direction="row" alignItems="center" spacing={3} flexWrap="wrap" useFlexGap>
                    <Typography variant="subtitle2" sx={{ minWidth: 80 }}>Assignment</Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar sx={{ width: 28, height: 28, bgcolor: "primary.main" }}>
                        <PersonIcon sx={{ fontSize: 16 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Assigned To</Typography>
                        <Typography variant="body2">{risk.assignedTo || "Unassigned"}</Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar sx={{ width: 28, height: 28, bgcolor: "secondary.main" }}>
                        <GroupIcon sx={{ fontSize: 16 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Team</Typography>
                        <Typography variant="body2">{risk.assignedTeam || "Unassigned"}</Typography>
                      </Box>
                    </Stack>
                    <Box sx={{ flex: 1 }} />
                    <Button size="small" variant="outlined">Reassign</Button>
                  </Stack>
                </CardContent>
              </Card>

              {/* Activity Timeline */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>Activity Timeline</Typography>
                  {risk.actions && risk.actions.length > 0 ? (
                    <Timeline sx={{ p: 0, m: 0 }}>
                      {risk.actions.map((action, index) => (
                        <TimelineItem key={action.id} sx={{ "&::before": { display: "none" }, minHeight: 48 }}>
                          <TimelineSeparator>
                            <TimelineDot
                              sx={{ m: 0 }}
                              color={
                                action.type === "resolve" ? "success" :
                                action.type === "escalate" ? "error" :
                                action.type === "acknowledge" ? "primary" : "grey"
                              }
                              variant={index === 0 ? "filled" : "outlined"}
                            />
                            {index < risk.actions!.length - 1 && <TimelineConnector />}
                          </TimelineSeparator>
                          <TimelineContent sx={{ py: 0.5, px: 1.5 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                              <Box>
                                <Typography variant="body2">{action.description}</Typography>
                                <Typography variant="caption" color="text.secondary">by {action.actor}</Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                {formatDistanceToNow(new Date(action.timestamp), { addSuffix: true })}
                              </Typography>
                            </Stack>
                          </TimelineContent>
                        </TimelineItem>
                      ))}
                    </Timeline>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No activity recorded yet</Typography>
                  )}
                </CardContent>
              </Card>
            </Stack>
          </Box>
        </Grid>

        {/* Right Column - Notes with full height */}
        <Grid size={{ xs: 12, lg: 4 }} sx={{ height: "100%" }}>
          <Card variant="outlined" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            {/* Header */}
            <Box sx={{ px: 2, pt: 2, pb: 1, flexShrink: 0 }}>
              <Typography variant="subtitle2">Notes</Typography>
            </Box>

            {/* Scrollable notes list */}
            <Box sx={{ flex: 1, overflow: "auto", px: 2 }}>
              {risk.notes && risk.notes.length > 0 ? (
                <Stack spacing={2}>
                  {risk.notes.map((note) => (
                    <Box key={note.id} sx={{ display: "flex", gap: 1.5 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: 12, flexShrink: 0 }}>
                        {note.author.charAt(0)}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                          <Typography variant="body2" fontWeight={500}>{note.author}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDistanceToNow(new Date(note.timestamp), { addSuffix: true })}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {note.content}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">No notes yet</Typography>
              )}
            </Box>

            {/* Add note input - fixed at bottom */}
            <Box sx={{ p: 2, borderTop: 1, borderColor: "divider", flexShrink: 0 }}>
              <Stack direction="row" spacing={1}>
                <TextField placeholder="Add a note..." size="small" fullWidth multiline maxRows={3} />
                <Button variant="contained" size="small" sx={{ minWidth: "auto", px: 1.5 }}>
                  <SendIcon fontSize="small" />
                </Button>
              </Stack>
            </Box>
          </Card>
        </Grid>
      </Grid>

      <CreateWorkOrderDialog
        open={woDialogOpen}
        onClose={() => setWoDialogOpen(false)}
        fromRisk={risk}
      />
    </Box>
  );
};
